import { useBeta } from '../useBeta';
import type { ConfigFacade } from './config.facade';
import {
  legacyMeRefSchema,
  type LegacyMeRefSchema,
} from './upload-configuration';

export class ConfigService implements ConfigFacade {
  private legacyMeRef: LegacyMeRefSchema | null = null;
  private beta = useBeta();

  setLegacyMeRef(maybeLegacyMeRef?: unknown) {
    const parsed = legacyMeRefSchema.safeParse(maybeLegacyMeRef);
    const fallback: LegacyMeRefSchema = {
      config: {
        global: {
          uploadConfiguration: {
            aiUploadDocs: {
              allowedExtensions: [],
              allowedMimeTypes: [],
            },

            aiUploadImages: {
              allowedExtensions: [],
              allowedMimeTypes: [],
            },
            imageUpload: {
              allowedExtensions: [],
              allowedMimeTypes: [],
            },
          },
        },
      },
    };
    if (!parsed.success) {
      console.warn(
        'Failed to fetch global upload configuration - uploads probably wont work',
      );
    }
    this.legacyMeRef = parsed.success ? parsed.data : fallback;
  }

  baseUrl(): string {
    const API_BASE_URL = 'https://api.app.teachino.io'; // this string will be replaced during production build

    const url = API_BASE_URL.includes('https')
      ? API_BASE_URL
      : 'http://localhost:3000';
    return url;
  }

  exportBaseUrl(): string {
    const baseUrl = this.baseUrl();

    if (
      !this.isTestEnvironment() &&
      baseUrl === 'https://api.app.teachino.io'
    ) {
      return 'https://dashboard.app.teachino.io';
    }
    return baseUrl;
  }

  baseAiUrl(): string {
    const AI_API_BASE_URL = 'https://aiapi.app.teachino.io'; // this string will be replaced during production build

    const aiUrl = AI_API_BASE_URL.includes('https')
      ? AI_API_BASE_URL
      : this.baseUrl(); //use same as base url if not set
    return aiUrl;
  }

  uploadConfig() {
    if (!this.legacyMeRef) {
      throw new Error(
        'uploadConfig can only be fetched for authenticated users',
      );
    }
    return this.legacyMeRef.config.global.uploadConfiguration;
  }

  iFrameBaseUrl(): string {
    const IFRAME_BASE_URL = 'https://embed.teachino.online';
    const url = IFRAME_BASE_URL.includes('https')
      ? IFRAME_BASE_URL
      : 'http://localhost:3000';
    return url;
  }

  isMixpanelActive(): boolean {
    let mpActive = true;
    const mpActivePlaceholder = 'true';
    let mpActiveString = mpActivePlaceholder;
    if (mpActiveString === 'true') {
      mpActiveString = 'true';
    }
    // eslint-disable-next-line eqeqeq
    if (mpActiveString != 'true') {
      mpActive = false;
    }
    return mpActive;
  }

  mixpanelEndpointUrl(): string {
    let mpEndpoint = 'https://mpp.app.teachino.io/';
    if (
      mpEndpoint ===
      this.obfuscateRuntimeReplacementVariableName('MIXPANEL_URL')
    ) {
      mpEndpoint = 'https://mpp.edudev.at';
    }
    return mpEndpoint;
  }

  mixpanelProjectKey(): string {
    let mpKey = '680904b37bf79e82cef99693ebe3cc5e';
    if (
      mpKey === this.obfuscateRuntimeReplacementVariableName('MIXPANEL_KEY')
    ) {
      mpKey = '564a3612134a8ca4cd7cf30a99cbf50b';
    }
    return mpKey;
  }

  studentAppBaseUrl(): string {
    let url = 'DEVOPS_ENV_STUDENT_APP_BASE_URL';
    if (
      url ===
      this.obfuscateRuntimeReplacementVariableName('STUDENT_APP_BASE_URL')
    ) {
      // url = 'http://localhost:4200';
      url = 'https://student.app.teachino.io';
    }
    return url;
  }

  // url where the webcomponent will be hosted
  webComponentUrl(): string {
    let wcUrl = 'DEVOPS_ENV_WC_URL';
    if (wcUrl === this.obfuscateRuntimeReplacementVariableName('WC_URL')) {
      wcUrl = 'http://localhost:8080';
    }
    return wcUrl;
  }

  traineeDiscountCode(): string {
    let discountCode = 'JdXYqjM8';
    if (
      discountCode ===
      this.obfuscateRuntimeReplacementVariableName('TRAINEE_DISCOUNT_CODE')
    ) {
      discountCode = 'bjiwXeZE';
    }
    return discountCode;
  }
  traineeDiscountCode2Year(): string {
    let discountCode = '';
    if (
      !discountCode ||
      discountCode ===
        this.obfuscateRuntimeReplacementVariableName(
          'TRAINEE_DISCOUNT_2_YEAR_CODE',
        )
    ) {
      discountCode = '267f1KYc';
    }
    return discountCode;
  }

  sleekplanProductId(): string {
    const SLEEKPLAN_DEFAULT_ID = '11608575'; // this string will be replaced during production build

    return SLEEKPLAN_DEFAULT_ID.includes('11608575')
      ? '11608575'
      : SLEEKPLAN_DEFAULT_ID;
  }

  sleekplanBaseUrl() {
    return 'https://client.sleekplan.com';
  }

  isTestEnvironment() {
    return this.beta.isBeta();
  }

  isObservabilityActive(): boolean {
    let observabilityEndpoint = 'true';
    if (
      observabilityEndpoint ===
      this.obfuscateRuntimeReplacementVariableName('OBSERVABILITY_ACTIVE')
    ) {
      observabilityEndpoint = 'false';
    }

    // eslint-disable-next-line eqeqeq
    return observabilityEndpoint == 'true';
  }

  observabilityUrl(): string {
    let observabilityEndpoint = 'https://oops.app.teachino.io/collect';
    if (
      observabilityEndpoint ===
      this.obfuscateRuntimeReplacementVariableName('OBSERVABILITY_URL')
    ) {
      observabilityEndpoint = '';
    }
    return observabilityEndpoint;
  }

  observabilityEnvironment(): string {
    let env = 'beta';
    if (
      env ===
      this.obfuscateRuntimeReplacementVariableName('OBSERVABILITY_ENVIRONMENT')
    ) {
      env = '';
    }
    return env;
  }

  observabilityApiKey(): string {
    let key = 'bf4501d51f25d0a8726bcf764635bbdf46ea6d577b2e1a66';
    if (
      key ===
      this.obfuscateRuntimeReplacementVariableName('OBSERVABILITY_API_KEY')
    ) {
      key = '';
    }
    return key;
  }

  /** defines what exactly gets logged
   * valid values:
   * dev => logs all console messages
   * prod => only logs errors
   * */
  observabilityProfile(): string {
    let key = 'prod';
    if (
      key ===
      this.obfuscateRuntimeReplacementVariableName('OBSERVABILITY_PROFILE')
    ) {
      key = '';
    }
    return key;
  }

  audioRecordingMaxSeconds(): number {
    const DEFAULT_MAX_SECONDS = 60;
    const placeholder = 'DEVOPS_ENV_AUDIO_RECORDING_MAX_SECONDS';
    if (
      placeholder ===
      this.obfuscateRuntimeReplacementVariableName(
        'AUDIO_RECORDING_MAX_SECONDS',
      )
    ) {
      return DEFAULT_MAX_SECONDS;
    }
    const parsed = parseInt(placeholder);
    return isNaN(parsed) || parsed <= 0 ? DEFAULT_MAX_SECONDS : parsed;
  }

  private obfuscateRuntimeReplacementVariableName(
    nameWithoutPrefix: string,
  ): string {
    return ['DEVOPS_ENV', nameWithoutPrefix].join('_');
  }
}
